'use client';

import { Session } from 'next-auth';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import {
    Alignable,
    Column,
    Columns,
    Container,
    Link,
    Row,
    Rows,
    Section,
    Slider,
    SliderItem,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';
import { UserType } from '@app-types/user';

import { ContractSelectionModal } from '@components/contract-selection-modal';
import { Legals } from '@components/legals';
import { SSIBlock } from '@components/ssi-block';
import { ForceLogin } from '@components/trigger-login';

import { MarketingsState } from '@hooks/use-marketings';

import { getCurrentPhone, getCurrentPhoneInCart } from '@helpers/cart';
import { PAGE_SLUG } from '@helpers/partials';
import { paths } from '@helpers/path';

import { OfferCardContainer, ProAlert } from '@home-mobile/components';
import { OfferWallViewTracking } from '@home-mobile/helpers';
import { useInitPhoneInCartRenewal } from '@home-mobile/hooks';

import { OfferRenewalSkeleton } from '../offer-skeleton';

type OfferWallRenewalContainerProps = {
    session?: Session;
    marketingDiscounts: MarketingsState;
    contractId?: string;
    openOfferDetails: (offer: MobilePlanType) => void;
    onAddToCart: (selectedOffer: MobilePlanType, fundingKey?: string) => Promise<void>;
    phoneInCart?: Product;
    setPhoneInCart: (phone?: Product) => void;
    isPro: boolean;
};

export function OfferWallRenewalContainer({
    session,
    marketingDiscounts,
    contractId,
    openOfferDetails,
    onAddToCart,
    phoneInCart,
    setPhoneInCart,
    isPro,
}: OfferWallRenewalContainerProps) {
    const [plansRenewalList, setPlansRenewalList] = useState<MobilePlanType[]>();
    const existingSearchParams = useSearchParams();
    const isShortList = useMemo(() => (plansRenewalList ? plansRenewalList.length <= 2 : true), [plansRenewalList]);
    const user = session?.user as UserType | undefined;

    // TODO: setPhoneInCart in the parent component to avoid code duplication (offer-wall-acquisition-container.tsx)
    useEffect(() => {
        if (plansRenewalList && plansRenewalList.length > 0) {
            const simulatedPhone = getCurrentPhone(plansRenewalList?.[0]?.simulatedCart);
            if (simulatedPhone) {
                getCurrentPhoneInCart().then((cartPhone) => {
                    setPhoneInCart({ ...cartPhone, ...simulatedPhone });
                });
            }
        }
    }, [plansRenewalList]);

    const isWithSmartphone = existingSearchParams.has('smartphone')
        ? existingSearchParams.get('smartphone') === 'true'
        : true;

    useEffect(() => {
        OfferWallViewTracking(true, isWithSmartphone, isPro, !!user, marketingDiscounts.FOYER);
    }, [user?.sub, isWithSmartphone]);

    useInitPhoneInCartRenewal(setPlansRenewalList, session, contractId, isPro);

    return (
        <>
            {!session && <ForceLogin />}
            <Section className='has-text-centered'>
                <Title markup={TitleMarkup.H1} level={TitleLevels.TWO}>
                    Choisissez votre nouveau forfait Bouygues Telecom
                </Title>
                <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} level={TextLevels.ONE}>
                    Faites évoluer votre offre avec l&apos;un de nos nouveaux forfaits Bouygues Telecom&nbsp;!
                </Text>

                <Columns align={Alignable.ALIGNED_CENTER}>
                    {phoneInCart ? (
                        <Column narrow>
                            <Text level={TextLevels.ONE}>
                                {phoneInCart?.brand} {phoneInCart?.name} {phoneInCart?.color} {phoneInCart?.capacity}
                            </Text>
                        </Column>
                    ) : (
                        <Column narrow className='is-loading'>
                            <Text level={TextLevels.ONE}>Lorem ipsum dolor sit amet</Text>
                        </Column>
                    )}

                    <Column narrow>
                        <Link href={paths.renewalPhoneWall}>Je veux changer de téléphone</Link>
                    </Column>
                </Columns>
            </Section>
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                {phoneInCart && plansRenewalList && plansRenewalList.length ? (
                    <Container>
                        <Columns align={Alignable.ALIGNED_CENTER} className='is-hidden-mobile'>
                            <Slider
                                className={`is-hidden-mobile is-slider ${isShortList ? 'is-justified-center' : ''}`}
                                doted
                                invertedDoted
                                arrowsOut
                                motionLess={isShortList}
                            >
                                {plansRenewalList.map((plan) => (
                                    <SliderItem className='is-paddingless' size={4} key={plan.gencode}>
                                        <OfferCardContainer
                                            user={user}
                                            openOfferDetails={openOfferDetails}
                                            phoneInCart={phoneInCart}
                                            plan={plan}
                                            isRenewal
                                            onAddToCart={onAddToCart}
                                            isConnected
                                            marketingDiscounts={marketingDiscounts}
                                            isSmallPrice={false}
                                            hatInLine={plansRenewalList?.some((p) => Boolean(p.planFlag || p.isOwned))}
                                            showBlockedPlanZone={true}
                                        />
                                    </SliderItem>
                                ))}
                                {isPro && isShortList && (
                                    <SliderItem className='is-paddingless' size={4}>
                                        <View flexable align={Alignable.ALIGNED_CENTER} className='is-fullheight'>
                                            <ProAlert />
                                        </View>
                                    </SliderItem>
                                )}
                            </Slider>
                        </Columns>
                        <Rows className='is-hidden-tablet' gap={8}>
                            {plansRenewalList.map((plan) => (
                                <Row key={plan.gencode}>
                                    <OfferCardContainer
                                        user={user}
                                        openOfferDetails={openOfferDetails}
                                        phoneInCart={phoneInCart}
                                        plan={plan}
                                        isRenewal
                                        isConnected
                                        onAddToCart={onAddToCart}
                                        marketingDiscounts={marketingDiscounts}
                                        isSmallPrice={false}
                                        showBlockedPlanZone={true}
                                        hatInLine={plansRenewalList.some((p) => Boolean(p.planFlag || p.isOwned))}
                                    />
                                </Row>
                            ))}
                            {isPro && isShortList && (
                                <Row>
                                    <ProAlert />
                                </Row>
                            )}
                        </Rows>
                    </Container>
                ) : (
                    <OfferRenewalSkeleton />
                )}
            </Section>

            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <SSIBlock tagId='renouv' url='/banniere_forfaits-mobiles_renouvellement' />
            </Section>
            <Section>
                <Container>
                    <Legals id='renouv-legal' url={PAGE_SLUG.OFFER_WALL_RENEWAL} />
                </Container>
            </Section>
            {!contractId && <ContractSelectionModal redirect={paths.plans} />}
        </>
    );
}
